import React, { useEffect, useMemo, useRef, useState } from 'react';

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  isOpen,
  onToggle,
  onClose,
  classic = false,
  label,
}) => {
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      return;
    }

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return options;
    }

    return options.filter((option) => {
      const haystacks = [
        option.label,
        option.value,
        ...(Array.isArray(option.aliases) ? option.aliases : []),
      ];
      return haystacks.some((h) =>
        String(h || '')
          .toLowerCase()
          .includes(normalizedQuery)
      );
    });
  }, [options, query]);

  const selectedOption = options.find((option) => option.value === value);

  if (classic) {
    return (
      <div ref={wrapperRef} className="relative">
        {label ? (
          <span className="mb-1 block text-[11px] font-body font-extrabold uppercase tracking-wide text-[#E91E63]">
            {label}
          </span>
        ) : null}
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center justify-between rounded-xl border-2 border-[#F8BBD0] bg-white px-3 py-2.5 text-left outline-none transition focus:border-[#E91E63]"
        >
          <span className="flex min-w-0 items-center">
            <svg
              className="mr-2 h-5 w-5 shrink-0 text-[#6B7280]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"
              />
            </svg>
            <span
              className={`truncate font-body text-sm font-bold uppercase ${
                selectedOption ? 'text-[#111827]' : 'text-[#6B7280]'
              }`}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          </span>
          <svg
            className={`h-5 w-5 shrink-0 text-[#E91E63] transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m19 9-7 7-7-7"
            />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute left-0 top-full z-[200] mt-1 w-full overflow-hidden rounded-xl border-2 border-[#F8BBD0] bg-white shadow-xl">
            <div className="flex items-center border-b border-[#E5E7EB] px-3 py-2">
              <svg
                className="mr-2 h-4 w-4 shrink-0 text-[#6B7280]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"
                />
              </svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
                placeholder={placeholder}
                className="w-full border-0 bg-transparent font-body text-sm text-[#111827] outline-none"
              />
            </div>
            <div className="max-h-56 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSelected = option.value === value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        onChange(option.value);
                        onClose();
                      }}
                      className={`w-full px-3 py-2.5 text-left font-body text-sm font-semibold transition-colors ${
                        isSelected
                          ? 'bg-[#FCE4EC] text-[#E91E63]'
                          : 'text-[#111827] hover:bg-[#FFF5F8]'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-3 font-body text-sm text-[#6B7280]">
                  No stops found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between rounded-lg border-2 border-superfeo-gray bg-superfeo-white p-2 text-left font-body text-xs text-superfeo-darkgray focus:border-superfeo-gold focus:outline-none sm:p-2.5 sm:text-sm"
      >
        <span className="flex min-w-0 items-center">
          <svg
            className="mr-2 h-4 w-4 shrink-0 text-superfeo-darkgray/70"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"
            />
          </svg>
          <span className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </span>
        <svg
          className={`h-4 w-4 text-superfeo-darkgray transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m19 9-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-0 z-[200] w-full overflow-hidden rounded-lg border-2 border-superfeo-gray bg-superfeo-white shadow-xl">
          <div className="flex items-center border-b border-superfeo-gray/40 px-3 py-2">
            <svg
              className="mr-2 h-4 w-4 shrink-0 text-superfeo-darkgray/70"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"
              />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              placeholder={placeholder}
              className="w-full border-0 bg-transparent font-body text-xs text-superfeo-darkgray outline-none sm:text-sm"
            />
          </div>

          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = option.value === value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      onClose();
                    }}
                    className={`w-full px-3 py-2.5 text-left font-body text-xs transition-colors duration-150 sm:text-sm ${
                      isSelected
                        ? 'bg-superfeo-pink/15 font-semibold text-superfeo-pink'
                        : 'text-superfeo-darkgray hover:bg-superfeo-gray/40'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-3 font-body text-xs text-superfeo-darkgray/70 sm:text-sm">
                No stops found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
